import { Activity, type ReactNode } from 'react';
import usePerformanceStore from '@core/lib/zustand/stores/general/performance-store';

/**
 * Props for the {@link PerfBoundary} component.
 */
type PerfBoundaryProps = {
  /** Motion-powered variant rendered when the `animations` perf category is on. */
  animated: ReactNode;
  /** Shadcn-only static variant rendered when the `animations` perf category is off. */
  fallback: ReactNode;
  /**
   * When true, both variants stay mounted via React 19 `<Activity>` so state
   * (form input, scroll position, DnD context, editor cursor) survives toggling
   * perf mode. Pays a ~2x DOM cost in this subtree. Default: false.
   */
  preserveState?: boolean;
};

/**
 * Performance-aware variant boundary. Switches between an animated `motion/react`
 * subtree and a shadcn-only fallback subtree based on the `animations` category
 * of {@link usePerformanceStore}.
 *
 * Two modes:
 * - **Conditional swap** (default): only the active variant is mounted. Cheapest,
 *   loses local state across toggle. Use for stateless / decorative components.
 * - **Activity dual-mount** (`preserveState`): both variants mount; the hidden one
 *   has its Effects cleaned up and is `display:none`-hidden, but its DOM + state
 *   survive. Updates to the hidden tree are deferred until React is idle. Use for
 *   forms mid-edit, DnD surfaces, editors, scrollable/expanded lists.
 *
 * @param animated - The motion/react-powered variant.
 * @param fallback - The shadcn-only static variant.
 * @param preserveState - Opt into Activity-based dual-mount for state preservation.
 */
function PerfBoundary({ animated, fallback, preserveState = false }: PerfBoundaryProps) {
  const animationsEnabled = usePerformanceStore((s) => s.categories.animations);

  if (!preserveState) {
    return <>{animationsEnabled ? animated : fallback}</>;
  }

  return (
    <>
      <Activity mode={animationsEnabled ? 'visible' : 'hidden'}>{animated}</Activity>
      <Activity mode={animationsEnabled ? 'hidden' : 'visible'}>{fallback}</Activity>
    </>
  );
}

export { PerfBoundary, type PerfBoundaryProps };
